import React, { useState } from "react";
import styled from "styled-components";
import { AiOutlineCheckCircle } from "react-icons/ai";
// Remove green accent and overlays, use neutral glassmorphism
const ACCENT = 'rgba(255,255,255,0.7)';
const ACCENT_LIGHT = 'rgba(55, 108, 11, 0.68)';

const HelpAdmin = () => {
  const [selectedSection, setSelectedSection] = useState("IA Profile");

  const steps = {
    "IA Profile": [
      "Go to the IA Profile section in the dashboard.",
      "Click on 'Add IA Profile' to register a new IA.",
      "Fill in required details such as IA Name, Location, Contact Info.",
      "Click 'Submit' to save the profile.",
      "To edit an existing IA Profile, click the edit icon next to the record."
    ],
    "IA Farmers": [
      "Navigate to the IA Farmers section.",
      "Click 'Add Farmer' to register a new farmer.",
      "Enter details such as Name, Farm Area, Crop Type, and Contact.",
      "Click 'Save' to register the farmer.",
      "To update a farmer's details, use the edit button next to their record."
    ],
    "IA Training": [
      "Open the IA Training module.",
      "Click 'Schedule Training' to create a new session.",
      "Enter the training details like Date, Trainer Name, and Participants.",
      "Click 'Confirm' to finalize the schedule.",
      "To view past trainings, go to 'Training History' in the menu."
    ],
    "IA Officers": [
      "Navigate to the IA Officers section in the dashboard.",
      "Click 'Add Officer' to register a new IA officer.",
      "Provide details such as Name, Position, Contact Number, and Assigned IA.",
      "Select the officer's status and upload any supporting documents if required.",
      "Click 'Save' to store the officer's profile, or use the edit icon to update records."
    ],
    "IA Machineries": [
      "Open the IA Machineries module from the sidebar menu.",
      "Click 'Add Machinery' to register a new equipment entry.",
      "Input details such as Equipment Name, Quantity, Condition, and Assigned IA.",
      "Upload supporting documents or photos if available, then review the summary.",
      "Press 'Save' to record the machinery, or select an existing row to update information."
    ],
    "IA Contracts": [
      "Navigate to the IA Contracts section in the dashboard navigation.",
      "Choose 'Add Contract' to start logging a new IA agreement.",
      "Fill out contract metadata including Contract Title, Duration, Funding Source, and IA coverage.",
      "Attach supporting files such as scanned contracts or approval letters, then review the entry.",
      "Click 'Save' to finalize, or open an existing row to amend milestones, status, or attachments."
    ]
  };

  return (
    <Container>
      <BackgroundImage />

      {/* Glass Sidebar */}
      <Sidebar>
        <SidebarTitle>Help Menu</SidebarTitle>
        {Object.keys(steps).map((item) => (
          <SidebarButton
            key={item}
            isActive={selectedSection === item}
            onClick={() => setSelectedSection(item)}
          >
            {item}
          </SidebarButton>
        ))}
      </Sidebar>

      {/* Glass Content Panel */}
      <Content>
        <GlassPanel>
          <ContentHeader>{selectedSection} - Steps</ContentHeader>
          {steps[selectedSection].map((step, index) => (
            <Step key={index}>
              <AiOutlineCheckCircle size={20} color={ACCENT_LIGHT} />
              <StepText>{step}</StepText>
            </Step>
          ))}
        </GlassPanel>
      </Content>
    </Container>
  );
};

export default HelpAdmin;

// === Styled Components with Glassmorphism ===

const Container = styled.div`
  display: flex;
  height: calc(100vh - 20px);
  margin-top: 20px;
  position: relative;
  overflow: hidden;
  padding: 0 18px 18px;
  background: #e7f2ec;

  @media (max-width: 1200px) {
    flex-direction: column;
    height: auto;
    min-height: calc(100vh - 20px);
    gap: 18px;
    overflow: visible;
  }

  @media (max-width: 768px) {
    margin-top: 12px;
    padding: 0 12px 18px;
  }

  @media (max-width: 480px) {
    padding: 0 10px 14px;
    gap: 14px;
  }
`;

const BackgroundImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  /* Remove green and image, use only subtle white transparent overlay */
  background: linear-gradient(180deg, rgba(255,255,255,0.18), rgba(255,255,255,0.12));
  z-index: 0;
`;

const Sidebar = styled.div`
  background: #e7f2ec;
  backdrop-filter: blur(16px);
  border-radius: 16px;
  box-shadow: 0 4px 32px rgba(0,0,0,0.08);
  width: 280px;
  padding: 22px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  margin: 18px 14px 18px 0;
  height: calc(100% - 36px);
  transition: transform 220ms ease;

  @media (max-width: 1200px) {
    width: 100%;
    margin: 0;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    padding: 18px 20px;
    height: auto;
  }

  @media (max-width: 992px) {
    flex-wrap: wrap;
    justify-content: center;
    overflow: visible;
  }

  @media (max-width: 768px) {
    padding: 16px;
    gap: 10px;
  }

  @media (max-width: 480px) {
    padding: 12px;
    gap: 8px;
  }
`;

const SidebarTitle = styled.h2`
  color: #222;
  text-align: center;
  margin-bottom: 24px;
  font-size: 20px;
  text-shadow: none;

  @media (max-width: 1200px) {
    width: 100%;
    text-align: left;
    margin: 0;
    font-size: 18px;
  }

  @media (max-width: 992px) {
    width: auto;
    flex: 0 0 auto;
    margin-right: 12px;
    white-space: nowrap;
  }

  @media (max-width: 768px) {
    font-size: 17px;
  }
`;

const SidebarButton = styled.button`
  display: block;
  width: 100%;
  padding: 12px 16px;
  color: #222;
  border: none;
  background: #e7f2ec;
  text-align: left;
  cursor: pointer;
  font-size: 15px;
  margin-bottom: 10px;
  border-radius: 10px;
  transition: all 180ms ease;
  position: relative;
  &:hover {
    transform: translateX(4px);
    background: #dbeee2;
  }
  ${({ isActive }) =>
    isActive &&
    `background: #d4e8dc; font-weight: 700; color: #222; box-shadow: inset 0 0 0 1px rgba(0,0,0,0.04);`}
`;

const Content = styled.div`
  background: #e7f2ec;
  backdrop-filter: blur(18px);
  border-radius: 18px;
  flex: 1;
  padding: 28px 36px;
  margin: 18px 18px 18px 0;
  z-index: 2;
  overflow-y: auto;
  max-height: calc(100vh - 56px);
  /* subtle scrollbar for content */
  &::-webkit-scrollbar {
    height: 8px;
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(219, 215, 215, 0.4);
    border-radius: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }

  @media (max-width: 1200px) {
    margin: 0;
    max-height: none;
  }

  @media (max-width: 992px) {
    padding: 24px;
  }

  @media (max-width: 768px) {
    padding: 20px;
    border-radius: 14px;
  }

  @media (max-width: 480px) {
    padding: 18px;
    border-radius: 12px;
  }
`;

/* Explicit reusable glassmorphism panel matching requested spec */
const GlassPanel = styled.div`
  /* slightly darker panel for improved contrast while keeping glass effect */
  background: linear-gradient(rgba(0,0,0,0.06), rgba(0,0,0,0.06)), rgba(255, 255, 255, 0.22);
  backdrop-filter: blur(16px) saturate(125%); /* retain strong blur but slightly reduced */
  -webkit-backdrop-filter: blur(16px) saturate(125%);
  border: 1px solid rgba(28, 10, 10, 0.54); /* slightly stronger soft border */
  box-shadow: 0 10px 36px rgba(0, 0, 0, 0.11); /* subtle depth */
  border-radius: 18px; /* rounded corners */
  padding: 20px;
  width: 100%;

  @media (max-width: 768px) {
    padding: 18px;
  }

  @media (max-width: 480px) {
    padding: 16px;
    border-radius: 14px;
  }
`;

const ContentHeader = styled.h2`
  margin-bottom: 18px;
  /* lighter header color for better contrast on the glass panel */
  color: #1f9b1fa0;
  font-size: 22px;
  text-shadow: 0 1px 1px rgba(17, 28, 14, 0.91);
  display: flex;
  flex-direction: column;

  @media (max-width: 992px) {
    font-size: 20px;
    margin-bottom: 16px;
  }

  @media (max-width: 480px) {
    font-size: 19px;
  }
`;

const Step = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;

  @media (max-width: 480px) {
    margin-bottom: 14px;
  }
`;

const StepText = styled.p`
  font-size: 15px;
  margin-left: 12px;
  color: #12140ed3;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 14px;
  }

  @media (max-width: 480px) {
    font-size: 13px;
    margin-left: 10px;
  }
`;

/* scrollbar styles moved into `Content` */